<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import maplibregl from 'maplibre-gl';
  import { MapController, dot } from './map/controller';
  import { SolverClient, type Progress } from './solver/client';
  import type { Candidate, GlideParams, ReachResult, RegionResult } from './solver/protocol';
  import { sources } from './sources';
  import { config } from './config';
  import { settings, plan, saveSettings, snapToUnits, planToQuery, loadView, saveView, welcomed, markWelcomed, type Mode, type Plan } from './lib/state.svelte';
  import { guessStartView } from './lib/startview';
  import { cellOf, distance, idxCenter, pathLength, type LngLat } from './lib/geo';
  import { aglBands, MARGIN_BANDS } from './lib/colors';
  import { smoothBands, paintBands, bandOutline } from './lib/smooth';
  import { computeProfile, type Profile } from './lib/profile';
  import { routeCrossings, formatLimit, type Crossing } from './lib/airspace';
  import { reachToKml, routeToGpx, routeToKml, download, type Route3D } from './lib/exporters';
  import { fmtAlt, fmtDist, fmtGr, fmtSpeed, mToAlt, altToM, altStep, compass } from './lib/units';
  import { offlineTiles, prefetch } from './lib/offline';
  import type { Airspace } from './sources/types';
  import ProfileChart from './components/ProfileChart.svelte';
  import Settings from './components/Settings.svelte';
  import About from './components/About.svelte';
  import FlightSetup, { type Editor } from './components/FlightSetup.svelte';
  import Welcome from './components/Welcome.svelte';
  import Icon from './components/Icon.svelte';
  import Stepper from './components/Stepper.svelte';

  type Sheet = 'main' | 'settings' | 'about';

  let mapEl: HTMLDivElement;
  let mc: MapController | undefined;
  const solver = new SolverClient();

  let sheet = $state<Sheet>('main');
  let sheetOpen = $state(true);
  let progress = $state<Progress | null>(null);
  let error = $state('');
  let notice = $state('');

  let reach = $state.raw<ReachResult | null>(null);
  let selected = $state<number | null>(null);
  let region = $state.raw<RegionResult | null>(null);
  let profile = $state.raw<Profile | null>(null);
  let crossings = $state.raw<Crossing[]>([]);
  let airspaces = $state.raw<Airspace[]>([]);
  let cursor = $state<number | null>(null);
  let offlineStatus = $state('');

  let editor = $state<Editor>(null);
  let reachCount = $state(0);
  let maxAgl = $state(0);
  const legend = $derived(aglBands(settings.units.alt, maxAgl));
  let showWelcome = $state(!welcomed() && !plan.takeoff && !plan.route.length && !plan.area);
  let picked = $state<{ name: string; at: LngLat } | null>(null);

  let query = $state('');
  let results = $state<{ name: string; detail: string; at: LngLat }[]>([]);
  let searching = $state(false);

  const u = $derived(settings.units);
  const basemaps = sources.basemaps.list();

  // ---- persistence -------------------------------------------------------------------------
  $effect(() => {
    const snapshot = $state.snapshot(settings);
    saveSettings(snapshot);
  });
  $effect(() => {
    const q = planToQuery($state.snapshot(plan) as Plan, $state.snapshot(settings));
    history.replaceState(null, '', q ? `?${q}` : location.pathname);
  });

  function glideParams(gr = settings.gr): GlideParams {
    return {
      gr,
      margin: settings.margin,
      launchRamp: settings.launchRamp,
      cell: settings.cell,
      wind: { speed: Math.max(0, settings.windSpeed), from: settings.windFrom, airspeed: settings.airspeed },
    };
  }

  // ---- reach map ----------------------------------------------------------------------------
  async function runReach() {
    if (!plan.takeoff) return;
    error = '';
    const req = {
      type: 'reach' as const,
      takeoff: $state.snapshot(plan.takeoff) as LngLat,
      startAlt: settings.startMode === 'fixed' ? settings.startAlt : null,
      radius: settings.radius,
      params: glideParams(),
    };
    try {
      const r = await solver.run<ReachResult>(req, (p) => (progress = p));
      if (!r) return; // cancelled
      reach = r;
      progress = null;
      if (fitNextReach) {
        fitNextReach = false;
        fitReach(r);
      }
      let n = 0;
      for (const a of r.alt) if (!Number.isNaN(a)) n++;
      reachCount = n;
      showReach();
      if (selected !== null && !Number.isNaN(r.alt[selected])) selectCell(selected);
      else clearSelection();
      console.info(`reach: ${r.spec.nx}x${r.spec.ny} cells in ${r.ms.toFixed(0)} ms`);
    } catch (e) {
      progress = null;
      error = e instanceof Error ? e.message : String(e);
    }
  }

  let fitNextReach = false;
  /** Zoom to the reached area after a new takeoff (not on setting tweaks). */
  function fitReach(r: ReachResult) {
    const pts: LngLat[] = [r.takeoff];
    for (let i = 0; i < r.alt.length; i += 7) if (!Number.isNaN(r.alt[i])) pts.push(idxCenter(r.spec, i));
    if (pts.length > 1) mc?.fitPoints(pts);
  }

  function routeCells(r: ReachResult, cell: number): number[] {
    const out = [cell];
    let cur = cell;
    for (let g = 0; r.parent[cur] >= 0 && g < 100000; g++) {
      cur = r.parent[cur];
      out.push(cur);
    }
    return out.reverse();
  }

  function selectCell(idx: number) {
    if (!reach) return;
    selected = idx;
    const cells = routeCells(reach, idx);
    const pts = cells.map((c) => idxCenter(reach!.spec, c));
    pts[0] = reach.takeoff;
    updateProfile(pts, null, reach.startAlt);
  }

  function clearSelection() {
    selected = null;
    if (plan.mode === 'reach') {
      profileSeq++;
      profile = null;
      crossings = [];
      mc?.setRoute([], null);
    }
  }

  // ---- route profile ------------------------------------------------------------------------
  let profileSeq = 0;
  async function updateProfile(points: LngLat[], gr: number | null, startAlt: number | null) {
    const seq = ++profileSeq;
    if (points.length < 2) {
      profile = null;
      crossings = [];
      mc?.setRoute(points, null);
      return;
    }
    const step = Math.min(90, Math.max(30, pathLength(points) / 400));
    try {
      const p = await computeProfile(
        points,
        {
          gr: gr ?? settings.gr,
          startAlt,
          margin: settings.margin,
          launchRamp: settings.launchRamp,
          wind: glideParams().wind,
          step,
        },
        (pts) => sources.terrain.elevations(pts, { resolution: step }),
      );
      if (seq !== profileSeq) return;
      profile = p;
      mc?.setRoute(
        points,
        p.samples.map((s) => ({ lng: s.at[0], lat: s.at[1], alt: s.alt, ground: s.ground, low: s.low })),
      );
      await checkAirspace(p, seq);
    } catch (e) {
      if (seq === profileSeq) error = `Profile: ${e instanceof Error ? e.message : e}`;
    }
  }

  async function checkAirspace(p: Profile, seq: number) {
    if (!settings.airspace) {
      crossings = [];
      return;
    }
    let w = Infinity,
      s = Infinity,
      e = -Infinity,
      n = -Infinity;
    for (const q of p.points) {
      w = Math.min(w, q[0]);
      e = Math.max(e, q[0]);
      s = Math.min(s, q[1]);
      n = Math.max(n, q[1]);
    }
    try {
      const list = await sources.airspace.query([w, s], [e, n]);
      if (seq !== profileSeq) return;
      crossings = routeCrossings(
        p.samples.map((x) => ({ d: x.d, at: x.at, alt: x.alt, ground: x.ground })),
        list,
      );
      refreshAirspaceLayer();
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    }
  }

  function routeStartAlt(): number | null {
    return settings.startMode === 'fixed' ? settings.startAlt : null;
  }

  function refreshRoute() {
    mc?.setMarkers('route', plan.mode === 'route' ? plan.route : [], (i) => dot('route-pin', String(i + 1)), {
      draggable: true,
      onClick: (i) => {
        plan.route = plan.route.filter((_, k) => k !== i);
        refreshRoute();
      },
      onDrag: (i, at) => {
        plan.route = plan.route.map((p, k) => (k === i ? at : p));
        refreshRoute();
      },
    });
    if (plan.mode === 'route') updateProfile(plan.route, plan.routeGr, routeStartAlt());
  }

  // ---- region search ------------------------------------------------------------------------
  async function runRegion() {
    if (!plan.landings.length) return;
    error = '';
    try {
      const r = await solver.run<RegionResult>(
        {
          type: 'region',
          landings: $state.snapshot(plan.landings) as LngLat[],
          params: glideParams(settings.regionGr),
          filters: {
            minHeight: settings.regionMinHeight,
            arrivalAgl: settings.margin,
            slope: settings.regionSlope ? [15, 35] : null,
            aspect: settings.regionAspect && settings.windSpeed > 0 ? settings.windFrom : null,
            aspectTol: 45,
          },
          limit: 12,
        },
        (p) => (progress = p),
      );
      progress = null;
      if (!r) return;
      region = r;
      showRegion();
      refreshRegionMarkers();
      if (r.candidates.length) mc?.fitPoints([...r.candidates.slice(0, 10).map((c) => c.at), ...(plan.landings as LngLat[])]);
      console.info(`region: ${r.spec.nx}x${r.spec.ny} cells, ${plan.landings.length} landings in ${r.ms.toFixed(0)} ms`);
    } catch (e) {
      progress = null;
      error = e instanceof Error ? e.message : String(e);
    }
  }

  const faceText = (faces: number[]) => {
    const dirs = faces.filter((f) => !Number.isNaN(f)).map((f) => compass(f));
    const flat = faces.some((f) => Number.isNaN(f));
    if (!dirs.length) return 'Flat top';
    return `Faces ${dirs.join(', ')}${flat ? ' + flat top' : ''}`;
  };

  /** Forward from a found launch: where else can you reach from it? */
  function glideFromCandidate(c: Candidate) {
    settings.startMode = 'ground';
    setMode('reach');
    setTakeoff(c.at);
  }

  function openCandidate(c: Candidate) {
    settings.startMode = 'ground';
    plan.routeGr = settings.regionGr;
    plan.route = c.path;
    setMode('route');
    mc?.fitPoints(c.path);
  }

  function refreshRegionMarkers() {
    mc?.setArea(null);
    mc?.setMarkers('landings', plan.mode === 'region' ? plan.landings : [], () => dot('landing-pin', 'L'), {
      draggable: true,
      onClick: (i) => {
        plan.landings = plan.landings.filter((_, k) => k !== i);
        refreshRegionMarkers();
      },
      onDrag: (i, at) => {
        plan.landings = plan.landings.map((p, k) => (k === i ? at : p));
        refreshRegionMarkers();
      },
    });
    mc?.setMarkers(
      'candidates',
      plan.mode === 'region' && region ? region.candidates.slice(0, 10).map((c) => c.at) : [],
      (i) => dot('cand-pin', String(i + 1)),
      { onClick: (i) => region && openCandidate(region.candidates[i]) },
    );
  }

  // ---- modes and taps -----------------------------------------------------------------------
  function setMode(m: Mode) {
    plan.mode = m;
    error = notice = '';
    if (m === 'reach') showReach();
    else mc?.setOverlay('reach', null, null);
    if (m === 'region') showRegion();
    else mc?.setOverlay('region', null, null);
    showTakeoff(m === 'reach' ? plan.takeoff : null);
    refreshRegionMarkers();
    if (m === 'route') refreshRoute();
    else {
      mc?.setMarkers('route', [], () => dot(''));
      if (m === 'reach' && selected !== null) selectCell(selected);
      else {
        profile = null;
        crossings = [];
        mc?.setRoute([], null);
      }
    }
    sheetOpen = true;
    sheet = 'main';
  }

  /** Smooth, rounded height bands for the reach area (display only; taps use the grid). */
  function showReach() {
    if (!reach) return mc?.setOverlay('reach', null, null);
    const { nx, ny } = reach.spec;
    const agl = new Float32Array(reach.alt.length);
    let top = 0;
    for (let i = 0; i < agl.length; i++) {
      agl[i] = reach.alt[i] - reach.elev[i];
      if (agl[i] > top) top = agl[i];
    }
    maxAgl = top;
    const b = aglBands(settings.units.alt, top);
    // Unreached cells sit below zero so the edge falls between the last reached cell and the next.
    const bands = smoothBands(agl, nx, ny, b.thresholds, -Math.max(30, settings.margin));
    mc?.setOverlay('reach', reach.spec, paintBands(bands, b.colors, nx, ny), bandOutline(reach.spec, bands[0]));
  }

  function showRegion() {
    if (!region) return mc?.setOverlay('region', null, null);
    const { nx, ny } = region.spec;
    const bands = smoothBands(region.margin, nx, ny, MARGIN_BANDS.thresholds, -50);
    mc?.setOverlay('region', region.spec, paintBands(bands, MARGIN_BANDS.colors, nx, ny), bandOutline(region.spec, bands[0]));
  }

  function showTakeoff(at: LngLat | null) {
    mc?.setMarkers('takeoff', at ? [at] : [], () => dot('takeoff-pin', '▲'), {
      draggable: true,
      onDrag: (_, p) => setTakeoff(p),
    });
  }

  function setTakeoff(at: LngLat) {
    plan.takeoff = at;
    selected = null;
    picked = null;
    showTakeoff(at);
    fitNextReach = true;
    runReach();
  }

  function dismissWelcome() {
    if (showWelcome) markWelcomed();
    showWelcome = false;
  }

  function onClick(at: LngLat) {
    dismissWelcome();
    layersOpen = false;
    if (plan.mode === 'reach') {
      const c = reach ? cellOf(reach.spec, at) : null;
      if (reach && c && !Number.isNaN(reach.alt[c.idx]) && c.idx !== cellOf(reach.spec, reach.takeoff)?.idx) selectCell(c.idx);
      else setTakeoff(at);
    } else if (plan.mode === 'route') {
      plan.route = [...plan.route, at];
      refreshRoute();
    } else {
      plan.landings = [...plan.landings, at];
      refreshRegionMarkers();
    }
  }

  function onLongPress(at: LngLat) {
    if (plan.mode !== 'reach') setMode('reach');
    setTakeoff(at);
    dismissWelcome();
  }

  function onRouteHover(at: LngLat | null) {
    if (!profile || !at) return;
    let best = 0;
    let bd = Infinity;
    profile.samples.forEach((s, i) => {
      const d = (s.at[0] - at[0]) ** 2 + (s.at[1] - at[1]) ** 2;
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    cursor = best;
    mc?.setCursor(profile.samples[best].at);
  }

  function onScrub(i: number | null) {
    if (!profile || i === null || !profile.samples[i]) {
      mc?.setCursor(null);
      return;
    }
    mc?.setCursor(profile.samples[i].at);
  }

  // ---- airspace layer -----------------------------------------------------------------------
  let airspaceSeq = 0;
  async function loadAirspaceView() {
    if (!mc || !settings.airspace || mc.map.getZoom() < 7) {
      if (!settings.airspace) {
        airspaces = [];
        refreshAirspaceLayer();
      }
      return;
    }
    const seq = ++airspaceSeq;
    try {
      const [sw, ne] = mc.bounds();
      const list = await sources.airspace.query(sw, ne);
      if (seq === airspaceSeq) {
        airspaces = list;
        refreshAirspaceLayer();
      }
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    }
  }

  function refreshAirspaceLayer() {
    const conflicts = new Set(crossings.filter((c) => c.conflict).map((c) => c.airspace.id));
    const byId = new Map(airspaces.map((a) => [a.id, a]));
    for (const c of crossings) byId.set(c.airspace.id, c.airspace);
    mc?.setAirspace(settings.airspace ? [...byId.values()] : [], conflicts, (a) => `${a.type}${a.icaoClass ? ` ${a.icaoClass}` : ''} ${a.name} ${formatLimit(a.floor)}–${formatLimit(a.ceiling)}`);
  }

  // ---- search, locate, share, export ----------------------------------------------------------
  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  function onSearchInput() {
    clearTimeout(searchTimer);
    if (query.trim().length < 3) {
      results = [];
      return;
    }
    searchTimer = setTimeout(async () => {
      searching = true;
      try {
        const c = mc?.map.getCenter();
        results = await sources.geocoder.search(query.trim(), c ? [c.lng, c.lat] : undefined);
      } catch (e) {
        error = e instanceof Error ? e.message : String(e);
      } finally {
        searching = false;
      }
    }, 350);
  }

  function pickPlace(name: string, at: LngLat) {
    results = [];
    query = '';
    mc?.flyTo(at, 13);
    picked = { name, at };
  }

  function locate() {
    if (!navigator.geolocation) return (error = 'Location is not available on this device.');
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const at: LngLat = [p.coords.longitude, p.coords.latitude];
        mc?.flyTo(at, 13);
        mc?.setMarkers('me', [at], () => dot('me-dot'));
      },
      (e) => (error = `Location: ${e.message}`),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  }

  async function share() {
    const url = location.href;
    try {
      if (navigator.share) await navigator.share({ title: 'Glide Planner', url });
      else {
        await navigator.clipboard.writeText(url);
        notice = 'Link copied.';
      }
    } catch {
      /* user dismissed */
    }
  }

  function route3d(name: string): Route3D | null {
    if (!profile) return null;
    // vertices at their glide altitude
    const pts: [number, number, number][] = [];
    let last = -1;
    for (const s of profile.samples) {
      if (s.leg !== last || s === profile.samples[profile.samples.length - 1]) {
        pts.push([s.at[0], s.at[1], s.alt]);
        last = s.leg;
      }
    }
    return { name, points: pts };
  }

  function exportReach() {
    if (!reach) return;
    const r = route3d('Selected route') ?? undefined;
    download('reach.kml', reachToKml(reach.spec, reach.alt, reach.elev, `Glide reach ${settings.gr}:1`, r), 'application/vnd.google-earth.kml+xml');
  }

  function exportRoute(kind: 'kml' | 'gpx') {
    const r = route3d('Glide route');
    if (!r) return;
    if (kind === 'kml') download('route.kml', routeToKml(r), 'application/vnd.google-earth.kml+xml');
    else download('route.gpx', routeToGpx(r), 'application/gpx+xml');
  }

  async function saveOffline() {
    const c = plan.takeoff ?? (mc ? ([mc.map.getCenter().lng, mc.map.getCenter().lat] as LngLat) : null);
    if (!c) return;
    const base = basemaps.find((b) => b.id === settings.basemap) ?? basemaps[0];
    const urls = offlineTiles(c, settings.radius, settings.cell, config.terrain.url, base.tiles ?? []);
    offlineStatus = `Downloading 0/${urls.length} tiles…`;
    const failed = await prefetch(urls, (d, t) => (offlineStatus = `Downloading ${d}/${t} tiles…`));
    offlineStatus = failed ? `Saved with ${failed} tiles missing.` : `Saved ${urls.length} tiles for offline use.`;
  }

  // ---- reactions to settings ------------------------------------------------------------------
  let rerunTimer: ReturnType<typeof setTimeout> | undefined;
  let settingsSeen = false;
  $effect(() => {
    // solver inputs
    void [settings.gr, settings.startMode, settings.startAlt, settings.margin, settings.launchRamp, settings.radius, settings.cell];
    void [settings.windSpeed, settings.windFrom, settings.airspeed];
    untrack(() => {
      // the first run is the initial state; onMount already starts the solve
      if (!settingsSeen) return void (settingsSeen = true);
      clearTimeout(rerunTimer);
      rerunTimer = setTimeout(() => {
        if (plan.takeoff) runReach();
        if (plan.mode === 'route') refreshRoute();
      }, 250);
    });
  });

  $effect(() => {
    const id = settings.basemap;
    const alt = settings.units.alt;
    const contours = settings.contours;
    untrack(() => {
      const b = basemaps.find((x) => x.id === id) ?? basemaps[0];
      mc?.setBasemap(b, alt, contours);
    });
  });

  $effect(() => {
    void settings.units.alt;
    untrack(() => plan.mode === 'reach' && showReach());
  });

  $effect(() => {
    const on = settings.threeD;
    untrack(() => mc?.set3D(on));
  });

  $effect(() => {
    void settings.airspace;
    untrack(() => {
      loadAirspaceView();
      if (profile) checkAirspace(profile, profileSeq);
    });
  });

  onMount(() => {
    const start = plan.takeoff ?? plan.route[0] ?? plan.landings[0] ?? plan.area?.[0];
    const view = start ? { center: start, zoom: 11 } : (loadView() ?? guessStartView());
    mc = new MapController(mapEl, sources.terrain, { click: onClick, longPress: onLongPress, routeHover: onRouteHover }, view);
    mc.map.on('moveend', () => {
      loadAirspaceView();
      const c = mc!.map.getCenter();
      saveView([c.lng, c.lat], mc!.map.getZoom());
    });
    if (import.meta.env.DEV) Object.assign(window, { __map: mc.map, __ml: maplibregl });
    mc.setBasemap(basemaps.find((b) => b.id === settings.basemap) ?? basemaps[0], settings.units.alt, settings.contours);
    setMode(plan.mode);
    if (plan.takeoff) setTakeoff(plan.takeoff);
    if (settings.threeD) mc.set3D(true);
    return () => mc?.map.remove();
  });

  let layersOpen = $state(false);

  // ---- bottom sheet drag -------------------------------------------------------------------
  let sheetEl: HTMLElement;
  let drag = $state<{ y0: number; base: number; offset: number; active: boolean } | null>(null);
  let dragged = false;
  /** Height of the handle + tabs: what stays visible when the panel is collapsed. */
  let peek = $state(96);
  function dragStart(e: PointerEvent) {
    if (innerWidth >= 900) return;
    const h = sheetEl.getBoundingClientRect().height;
    drag = { y0: e.clientY, base: sheetOpen ? 0 : h - peek - 6, offset: sheetOpen ? 0 : h - peek - 6, active: false };
    dragged = false;
  }
  function dragMove(e: PointerEvent) {
    if (!drag) return;
    const dy = e.clientY - drag.y0;
    if (!drag.active && Math.abs(dy) > 8) {
      drag.active = dragged = true;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    }
    if (drag.active) {
      const h = sheetEl.getBoundingClientRect().height;
      drag.offset = Math.min(h - peek - 6, Math.max(0, drag.base + dy));
    }
  }
  function dragEnd(e: PointerEvent) {
    if (!drag) return;
    if (drag.active) {
      const dy = e.clientY - drag.y0;
      if (sheetOpen && dy > 50) sheetOpen = false;
      else if (!sheetOpen && dy < -30) sheetOpen = true;
      setTimeout(() => (dragged = false), 0);
    }
    drag = null;
  }

  const cellInfo = $derived.by(() => {
    if (!reach || selected === null) return null;
    const alt = reach.alt[selected];
    const ground = reach.elev[selected];
    const pts = routeCells(reach, selected).map((c) => idxCenter(reach!.spec, c));
    pts[0] = reach.takeoff;
    return { alt, ground, agl: alt - ground, length: pathLength(pts), direct: distance(reach.takeoff, idxCenter(reach.spec, selected)) };
  });

  const progressPct = $derived(progress ? Math.min(100, (100 * progress.done) / Math.max(1, progress.total)) : 0);
  const conflicts = $derived(crossings.filter((c) => c.conflict));
</script>

<div class="app">
  <div class="map" bind:this={mapEl}></div>

  <header class="top">
    <div class="search">
      <span class="search-icon"><Icon name="search" size={18} /></span>
      <input
        type="search"
        placeholder="Search places"
        bind:value={query}
        oninput={onSearchInput}
        aria-label="Search places"
        enterkeyhint="search"
      />
      {#if searching}<span class="spin" aria-hidden="true"></span>{/if}
      {#if results.length}
        <ul class="results">
          {#each results as r, i (i)}
            <li><button onclick={() => pickPlace(r.name, r.at)}><b>{r.name}</b> <small>{r.detail}</small></button></li>
          {/each}
        </ul>
      {/if}
    </div>
    <button class="icon" class:on={sheet === 'settings'} title="Settings" aria-label="Settings" onclick={() => ((sheet = sheet === 'settings' ? 'main' : 'settings'), (sheetOpen = true))}><Icon name="sliders" /></button>
    <button class="icon" class:on={sheet === 'about'} title="About and data sources" aria-label="About and data sources" onclick={() => ((sheet = sheet === 'about' ? 'main' : 'about'), (sheetOpen = true))}><Icon name="info" /></button>
  </header>

  <div class="overlays-left">
    {#if progress}
      <div class="progress" role="status">
        <div class="bar"><div style="width:{progressPct}%"></div></div>
        <span>{progress.phase === 'terrain' ? 'Loading terrain' : progress.phase === 'solve' ? 'Computing' : progress.phase === 'refine' ? 'Adding detail' : `Searching ${progress.phase}`}…</span>
        <button class="btn small" onclick={() => (solver.cancelAll(), (progress = null))}>Cancel</button>
      </div>
    {/if}
    {#if settings.windSpeed > 0 && plan.mode !== 'region'}
      <button class="badge" title="Wind (tap to change)" onclick={() => ((sheet = 'main'), (editor = 'wind'), (sheetOpen = true))}>
        <svg width="18" height="18" viewBox="-10 -10 20 20" aria-hidden="true" style="transform: rotate({settings.windFrom}deg)">
          <path d="M0 -8 V6 M-4 2 L0 7 L4 2" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        {fmtSpeed(settings.windSpeed, u)} from {compass(settings.windFrom)}
      </button>
    {/if}
    {#if plan.mode === 'reach' && reach}
      <div class="legend" aria-label="Height above ground legend">
        <small>Height above ground ({u.alt})</small>
        <div class="swatches">
          {#each legend.colors as c, i (c)}
            <span style="background:{c}"></span>
          {/each}
        </div>
        <div class="ticks">
          {#each legend.labels as l, i (l)}<small>{l.toLocaleString()}{i === legend.labels.length - 1 ? '+' : ''}</small>{/each}
        </div>
      </div>
    {/if}
  </div>

  <div class="fabs" role="toolbar" aria-label="Map tools">
    <button class="fab" title="Show my location" aria-label="Show my location" onclick={locate}><Icon name="locate" /></button>
    <div class="layers-wrap">
      <button class="fab" class:on={layersOpen} title="Map style" aria-label="Map style" aria-expanded={layersOpen} onclick={() => (layersOpen = !layersOpen)}><Icon name="layers" /></button>
      {#if layersOpen}
        <div class="layers" role="menu">
          {#each basemaps as b (b.id)}
            <button role="menuitemradio" aria-checked={settings.basemap === b.id} class:on={settings.basemap === b.id} onclick={() => (settings.basemap = b.id)}>{b.name}</button>
          {/each}
          <label><input type="checkbox" bind:checked={settings.contours} /> Contour lines</label>
          <label><input type="checkbox" bind:checked={settings.airspace} /> Airspace</label>
        </div>
      {/if}
    </div>
    <button class="fab" class:on={settings.threeD} title="3D view" aria-label="3D view" aria-pressed={settings.threeD} onclick={() => (settings.threeD = !settings.threeD)}><Icon name="cube" /></button>
    <button class="fab" title="Share this plan" aria-label="Share this plan" onclick={share}><Icon name="share" /></button>
  </div>

  {#if picked && plan.mode === 'reach'}
    <div class="pill" role="dialog" aria-label="Use searched place">
      <span>{picked.name}</span>
      <button class="btn primary small" onclick={() => picked && setTakeoff(picked.at)}>Set takeoff here</button>
      <button class="x" aria-label="Dismiss" onclick={() => (picked = null)}><Icon name="x" size={16} /></button>
    </div>
  {/if}

  <section class="sheet" class:open={sheetOpen} class:dragging={drag !== null} bind:this={sheetEl} style="--peek: {peek + 6}px;{drag ? ` transform: translateY(${drag.offset}px)` : ''}">
    <!-- Drag (or swipe) the top of the panel up/down to open or close it; tap the handle to toggle. -->
    <div class="sheet-top" role="presentation" bind:offsetHeight={peek} onpointerdown={dragStart} onpointermove={dragMove} onpointerup={dragEnd} onpointercancel={dragEnd}>
    <button class="grab" aria-label={sheetOpen ? 'Collapse panel' : 'Expand panel'} aria-expanded={sheetOpen} onclick={() => !dragged && (sheetOpen = !sheetOpen)}>
      <span></span>
      <i class:up={!sheetOpen}><Icon name="chevron" size={16} /></i>
    </button>
    {#if sheet === 'main'}
      <div class="modes" role="tablist">
        {#each [['reach', 'Glide area'], ['route', 'Route'], ['region', 'Find launch']] as [m, label] (m)}
          <button role="tab" aria-selected={plan.mode === m} class:on={plan.mode === m} onclick={() => setMode(m as Mode)}>{label}</button>
        {/each}
      </div>
    {:else}
      <div class="sheet-head">
        <h2>{sheet === 'settings' ? 'Settings' : 'About'}</h2>
        <button class="icon flat" aria-label="Close" onclick={() => (sheet = 'main')}><Icon name="x" /></button>
      </div>
    {/if}
    </div>

    <div class="content">
      {#if error}<p class="err">{error} <button class="link" onclick={() => (error = '')}>dismiss</button></p>{/if}
      {#if notice}<p class="hint">{notice} <button class="link" onclick={() => (notice = '')}>ok</button></p>{/if}

      {#if sheet === 'settings'}
        <Settings onoffline={saveOffline} {offlineStatus} />
      {:else if sheet === 'about'}
        <About />
      {:else}
        {#if showWelcome}
          <Welcome ondone={dismissWelcome} />
        {/if}

        {#if plan.mode !== 'region'}
          <FlightSetup
            bind:open={editor}
            forecastAt={() => plan.takeoff ?? plan.route[0] ?? null}
            flyingAlt={() => reach?.startAlt ?? settings.startAlt}
            grOverride={plan.mode === 'route' ? plan.routeGr : null}
            onclearoverride={() => ((plan.routeGr = null), refreshRoute())}
          />
        {/if}

        {#if plan.mode === 'reach'}
          {#if !plan.takeoff}
            {#if !showWelcome}<p class="hint">Search for your launch above, or tap it on the map.</p>{/if}
          {:else if cellInfo}
            <div class="stats">
              <div><small>Altitude</small><b>{fmtAlt(cellInfo.alt, u)}</b></div>
              <div><small>Above ground</small><b>{fmtAlt(cellInfo.agl, u)}</b></div>
              <div><small>Ground</small><b>{fmtAlt(cellInfo.ground, u)}</b></div>
              <div><small>Route</small><b>{fmtDist(cellInfo.length, u)}</b></div>
            </div>
          {:else if reach && reachCount <= 3}
            <div class="callout">
              <b>Nowhere to glide from here.</b>
              {#if settings.startMode === 'ground'}
                Launching from the ground needs a slope steeper than your glide, and this spot ({fmtAlt(reach.startAlt, u)}) is too
                flat. Tap a hillside launch, or start higher.
                <div class="actions"><button class="btn small" onclick={() => ((settings.startMode = 'fixed'), (settings.startAlt = Math.max(settings.startAlt, reach!.startAlt + 1000)), snapToUnits(settings), (editor = 'start'))}>Start at altitude</button></div>
              {:else}
                The start altitude is too low to clear the terrain here. Raise it, or try a lower glide margin.
              {/if}
            </div>
          {:else if reach}
            <p class="hint">Start at {fmtAlt(reach.startAlt, u)}. Tap anywhere coloured to see the route there. Drag ▲ to move the takeoff.</p>
          {/if}
          {#if reach}
            <div class="actions">
              <button class="btn" onclick={exportReach}><Icon name="download" size={16} /> Glide area (KML)</button>
              {#if profile}<button class="btn" onclick={() => exportRoute('gpx')}><Icon name="download" size={16} /> Route (GPX)</button>{/if}
            </div>
          {/if}
        {:else if plan.mode === 'route'}
          {#if plan.route.length < 2}
            <p class="hint">Tap the map to add points from launch to landing. Drag a point to move it; tap it to remove it.</p>
          {/if}
          {#if profile}
            <div class="stats">
              <div><small>Arrival</small><b class:bad={profile.arrivalAgl < settings.margin}>{fmtAlt(profile.arrivalAgl, u)}</b></div>
              <div><small>Needs glide</small><b>{fmtGr(profile.requiredGr)}</b></div>
              <div><small>Length</small><b>{fmtDist(profile.total, u)}</b></div>
              <div><small>Clearance</small><b class:bad={!profile.ok}>{profile.unflyable ? 'Unflyable leg' : profile.ok ? 'OK' : 'Too low'}</b></div>
            </div>
          {/if}
          <div class="actions">
            {#if plan.route.length}<button class="btn" onclick={() => ((plan.route = []), (plan.routeGr = null), refreshRoute())}><Icon name="trash" size={16} /> Clear</button>{/if}
            {#if profile}
              <button class="btn" onclick={() => exportRoute('gpx')}><Icon name="download" size={16} /> GPX</button>
              <button class="btn" onclick={() => exportRoute('kml')}><Icon name="download" size={16} /> KML</button>
            {/if}
          </div>
        {:else}
          <p class="hint">
            {#if !plan.landings.length}Tap the map to place your landing (add more if you like). The search covers everywhere you could glide in from.
            {:else}Find every launch that can glide to {plan.landings.length > 1 ? 'one of your landings' : 'your landing'}. Tap a landing pin to remove it.{/if}
          </p>
          <div class="filters">
            <div class="frow">
              <span>Glide ratio</span>
              <Stepper label="Region glide ratio" value={settings.regionGr} step={0.5} min={2} max={11} decimals={1} suffix=":1" onchange={(v) => (settings.regionGr = v)} />
            </div>
            <div class="frow">
              <span>Launch at least</span>
              <Stepper
                label="Minimum height above landing"
                value={Math.round(mToAlt(settings.regionMinHeight, u))}
                step={altStep(u) * 2}
                min={0}
                max={Math.round(mToAlt(3000, u))}
                suffix=" {u.alt}"
                onchange={(v) => (settings.regionMinHeight = altToM(v, u))}
              />
            </div>
            <span class="hint">above the landing</span>
            <label><input type="checkbox" bind:checked={settings.regionSlope} /> Launchable slope (15–35°)</label>
            <label
              ><input type="checkbox" bind:checked={settings.regionAspect} disabled={settings.windSpeed <= 0} /> Facing the wind
              {#if settings.windSpeed > 0}({compass(settings.windFrom)}){:else}<small>(set a wind first)</small>{/if}</label
            >
          </div>
          <div class="actions">
            <button class="btn primary" disabled={!plan.landings.length || !!progress} onclick={runRegion}>Find launches</button>
            {#if plan.landings.length}<button class="btn" onclick={() => ((plan.landings = []), (region = null), setMode('region'))}>Start over</button>{/if}
          </div>
          {#if region && !region.candidates.length && !progress}
            <div class="callout">
              <b>No launches found.</b>
              {#if region.highestAbove < settings.regionMinHeight}
                The highest ground within gliding range is only {fmtAlt(region.highestAbove, u)} above your landing, below your
                {fmtAlt(settings.regionMinHeight, u)} minimum.
                <div class="actions">
                  <button class="btn small" onclick={() => ((settings.regionMinHeight = Math.max(0, Math.floor((region!.highestAbove * 0.6) / altToM(altStep(u), u)) * altToM(altStep(u), u))), runRegion())}>
                    Minimum {fmtAlt(Math.max(0, Math.floor((region.highestAbove * 0.6) / altToM(altStep(u), u)) * altToM(altStep(u), u)), u)} and search again
                  </button>
                </div>
              {:else}
                Ground up to {fmtAlt(region.highestAbove, u)} above the landing exists here, but none of it can glide there at
                {settings.regionGr}:1{settings.regionSlope || settings.regionAspect ? ' with the slope and wind filters on' : ''}.
                <div class="actions">
                  {#if settings.regionGr < 11}
                    <button class="btn small" onclick={() => ((settings.regionGr = Math.min(11, settings.regionGr + 2)), runRegion())}>Try {Math.min(11, settings.regionGr + 2)}:1</button>
                  {/if}
                  {#if settings.regionSlope || settings.regionAspect}
                    <button class="btn small" onclick={() => ((settings.regionSlope = false), (settings.regionAspect = false), runRegion())}>Turn filters off</button>
                  {/if}
                </div>
              {/if}
            </div>
          {/if}
          {#if region?.candidates.length}
            <p class="hint">
              {region.candidates.length} launch zone{region.candidates.length === 1 ? '' : 's'}, best first. Shaded on the map: darker means more height to spare.
            </p>
            <ol class="cands">
              {#each region.candidates as c, i (c.idx)}
                <li>
                  <button class="zone" onclick={() => openCandidate(c)}>
                    <b>{i + 1}</b>
                    <span>
                      {faceText(c.faces)}, {c.minGround === c.maxGround ? fmtAlt(c.ground, u) : `${fmtAlt(c.minGround, u).replace(/ \w+$/, '')}–${fmtAlt(c.maxGround, u)}`}
                      <small>
                        Best spot {fmtAlt(c.ground, u)}{Number.isNaN(c.aspect) ? '' : `, ${Math.round(c.slope)}°`}, arrives {fmtAlt(c.margin, u)} higher than needed{plan.landings.length > 1 ? ` at landing ${c.landing + 1}` : ''}.
                        {c.areaKm2 >= 0.1 ? `${c.areaKm2.toFixed(1)} km²` : ''}
                      </small>
                    </span>
                  </button>
                  <div class="zone-actions">
                    <button class="btn small" onclick={() => openCandidate(c)}>Profile to landing</button>
                    <button class="btn small" onclick={() => glideFromCandidate(c)}>Glide area from here</button>
                  </div>
                </li>
              {/each}
            </ol>
          {/if}
        {/if}

        {#if profile && plan.mode !== 'region'}
          {#if conflicts.length}
            <p class="err">Enters airspace: {conflicts.map((c) => `${c.airspace.type} ${c.airspace.name} (${formatLimit(c.airspace.floor)}–${formatLimit(c.airspace.ceiling)})`).join('; ')}</p>
          {/if}
          <div class="chart-wrap">
            <ProfileChart {profile} {crossings} units={u} margin={settings.margin} {cursor} onscrub={onScrub} />
          </div>
        {/if}
      {/if}
    </div>
  </section>
</div>
